'use client'

import * as React from 'react'
import { useForm } from 'react-hook-form'
import * as Form from '@repo/web-ui/components/form/form'
import { useLocale, useTranslations } from 'next-intl'
import { useUser } from '@repo/cf-user/hooks/use-user-data'

import { Button } from '@web/components/prod/button/button'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'

export interface StoreApplicationFormValues {
  fullName: string
  designerName: string
  productLinks: string
  portfolioLinks: string
  motivation: string
  referrerName?: string
  terms_and_conditions: boolean
}

const URL_PATTERN =
  /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/

const validateUrl = (value: string): boolean | string => {
  if (!value || value.trim() === '') {
    return true
  }
  const links = value
    .split(/[\n,]/)
    .map((link: string) => link.trim())
    // oxlint-disable-next-line unicorn/prefer-native-coercion-functions
    .filter((link: string) => link)
  return (
    links.some((link: string) => URL_PATTERN.test(link)) || 'Please provide at least one valid URL'
  )
}

interface StoreApplicationFormProps {
  onSubmit: (values: StoreApplicationFormValues) => void
  isPending: boolean
}

export function StoreApplicationForm({ onSubmit, isPending }: StoreApplicationFormProps) {
  const t = useTranslations('my_account.open_store_modal')
  const { data: user } = useUser()
  const locale = useLocale()

  const form = useForm<StoreApplicationFormValues>({
    defaultValues: {
      fullName: user?.firstName || user?.lastName ? `${user?.firstName} ${user?.lastName}` : '',
      designerName: '',
      productLinks: '',
      portfolioLinks: '',
      motivation: '',
      referrerName: '',
      terms_and_conditions: false,
    },
    mode: 'onChange',
  })

  const formValues = form.watch()

  const handleSubmit = (values: StoreApplicationFormValues) => {
    form.clearErrors()
    onSubmit(values)
  }

  const isSubmitDisabled =
    isPending ||
    Object.keys(form.formState.errors).length > 0 ||
    !formValues.fullName?.trim() ||
    !formValues.designerName?.trim() ||
    !formValues.productLinks?.trim() ||
    !formValues.portfolioLinks?.trim() ||
    !formValues.motivation?.trim() ||
    !formValues.terms_and_conditions

  return (
    <>
      <h3 className="mb-8 text-center text-xl font-bold">{t('title')}</h3>
      <Form.Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
          <div className="space-y-5">
            <Form.FormField
              name="fullName"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.full_name.label')}</div>
                  <Form.FormControl>
                    <input
                      {...field}
                      required
                      className="h-10 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      placeholder={t('fields.full_name.placeholder')}
                    />
                  </Form.FormControl>
                </Form.FormItem>
              )}
            />

            <Form.FormField
              name="designerName"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.designer_name.label')}</div>
                  <Form.FormControl>
                    <input
                      {...field}
                      required
                      className="h-10 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      placeholder={t('fields.designer_name.placeholder')}
                    />
                  </Form.FormControl>
                </Form.FormItem>
              )}
            />

            <Form.FormField
              name="productLinks"
              control={form.control}
              rules={{
                required: t('fields.product_links.validation_error'),
                validate: { validUrl: validateUrl },
              }}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.product_links.label')}</div>
                  <Form.FormControl>
                    <textarea
                      {...field}
                      required
                      className="h-20 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      placeholder={t('fields.product_links.placeholder')}
                      onChange={e => {
                        field.onChange(e)
                        form.trigger('productLinks')
                      }}
                    />
                  </Form.FormControl>
                  {form.formState.errors.productLinks ? (
                    <div className="text-sm text-red-100">
                      {t('fields.product_links.validation_error')}
                    </div>
                  ) : null}
                </Form.FormItem>
              )}
            />

            <Form.FormField
              name="portfolioLinks"
              control={form.control}
              rules={{
                required: t('fields.portfolio_links.validation_error'),
                validate: { validUrl: validateUrl },
              }}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.portfolio_links.label')}</div>
                  <Form.FormControl>
                    <textarea
                      {...field}
                      required
                      className="h-20 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      placeholder={t('fields.portfolio_links.placeholder')}
                      onChange={e => {
                        field.onChange(e)
                        form.trigger('portfolioLinks')
                      }}
                    />
                  </Form.FormControl>
                  {form.formState.errors.portfolioLinks ? (
                    <div className="text-sm text-red-100">
                      {t('fields.portfolio_links.validation_error')}
                    </div>
                  ) : null}
                </Form.FormItem>
              )}
            />

            <Form.FormField
              name="motivation"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.motivation.label')}</div>
                  <Form.FormControl>
                    <textarea
                      {...field}
                      required
                      className="h-20 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      placeholder={t('fields.motivation.placeholder')}
                    />
                  </Form.FormControl>
                </Form.FormItem>
              )}
            />

            <Form.FormField
              name="referrerName"
              control={form.control}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.referrer_name.label')}</div>
                  <Form.FormControl>
                    <input
                      {...field}
                      className="h-10 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      placeholder={t('fields.referrer_name.placeholder')}
                    />
                  </Form.FormControl>
                </Form.FormItem>
              )}
            />

            <Form.FormField
              name="terms_and_conditions"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.FormItem>
                  <div className="mb-1 font-medium">{t('fields.terms_and_conditions.title')}</div>
                  <label className="flex items-start gap-2">
                    <Form.FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        className="mt-1 h-4 w-4 shrink-0 rounded border border-gray-300"
                      />
                    </Form.FormControl>
                    <span className="text-sm">
                      {t.rich('fields.terms_and_conditions.label', {
                        link: (chunks: React.ReactNode) => (
                          <Link
                            target="_blank"
                            className="text-cf-theme-green-700 underline"
                            href={addOrReplaceLocaleInUrl(AppRoutes.SINGLE_SALES_LICENCE, locale)}
                          >
                            {chunks}
                          </Link>
                        ),
                      })}
                    </span>
                  </label>
                </Form.FormItem>
              )}
            />
          </div>
          <div className="mt-6 flex ">
            <Button
              type="submit"
              variant="primary-green"
              className="w-full font-bold uppercase"
              disabled={isSubmitDisabled}
            >
              {t('submit_button')}
            </Button>
          </div>
        </form>
      </Form.Form>
    </>
  )
}
